import { ChangeEvent, useState } from "react"

import { Box, Switch } from "@material-ui/core"
import ConfirmationModal, {
  ModalInfo,
} from "components/Modals/ConfirmationModal"
import { DateRangeCell, NumberCell, Rable, TextCell } from "components/Rable"
import {
  createLoading,
  deleteLoading,
  fetchLoadings,
  patchLoading,
} from "api/ratecard"
import { css, cx } from "@emotion/css"
import { queryCache, useMutation, useQuery } from "react-query"

import { ApiError } from "api/http/utils"
import Button from "ui/buttons/Button"
import { Col } from "components/Rable/types"
import { IErrors } from "../../Packages/Details"
import { RateCardLoading } from "models/ratecard"
import { useAuthenticated } from "providers/A12n"
import { useI18n } from "providers/I18n"
import { useParams } from "react-router-dom"
import { useSendFeedBack } from "components/FeedbackSnackbar"
import { useToggle } from "hooks/useToggle"

const cols: Col<RateCardLoading>[] = [
  {
    id: "rate.card.loading.description",
    cell: TextCell,
    name: "description",
    //
    align: "left",
    width: 125,
    existsInCreate: true,
    required: true,
  },
  {
    id: "rate.card.loading.percentage",
    cell: NumberCell,
    name: "percentage",
    //
    align: "left",
    existsInCreate: true,
    required: true,
  },
  {
    id: "rate.card.loading.dateRange",
    cell: DateRangeCell,
    name: "dateRange",
    getValue: (x) => ({
      startDate: x.validationPeriodStart,
      endDate: x.validationPeriodEnd,
    }),
    getOutput: (x) => ({
      validationPeriodStart: x.startDate,
      validationPeriodEnd: x.endDate,
    }),
    //
    align: "left",
    existsInCreate: true,
    required: true,
  },
]

const newRowCols = cols?.filter((c) => c.existsInCreate)
const requiredCols = cols?.filter((c) => c.required)

type Props = {
  checked: boolean
  onSwitchChange: (e: ChangeEvent<HTMLInputElement>, checked: boolean) => void
  activateErrors?: IErrors
}

export const LoadingTable = (props: Props) => {
  const { t } = useI18n()
  const { hasAuthority } = useAuthenticated()
  const { id } = useParams<{ id: string }>()
  const { checked, onSwitchChange } = props
  const sendFeedback = useSendFeedBack()
  const newRowToggle = useToggle()
  const [modalInfo, setModalInfo] = useState<ModalInfo>()
  //eslint-disable-next-line
  const [tableErrors, setTableErrors] = useState<
    Record<string, string>[] | undefined
  >(undefined)
  const isModalOpen = Boolean(modalInfo)
  const actionAllowed = hasAuthority(["AUTH_ACCESS_MARKET_FINANCE"])

  // fetch loading table
  const { data: loadings } = useQuery(["rate-card-loadings", id], fetchLoadings)

  // create loading row
  const [create] = useMutation(createLoading, {
    onSuccess: () => {
      queryCache.invalidateQueries(["rate-card-loadings", id])
      // onClose()
    },
    onError: (err: ApiError) => {
      sendFeedback(t(err?.message), "error")
      setTableErrors(err.fields?.map((e: any) => e))
    },
  })

  // update loadings table
  const [patch] = useMutation(patchLoading, {
    onSuccess: () => {
      queryCache.invalidateQueries(["rate-card-loadings", id])
      setTableErrors(undefined)
    },
    onError: (e: ApiError) => {
      sendFeedback(t(e?.message), "error")
    },
  })

  // delete loadings row
  const [deleteRow] = useMutation(deleteLoading, {
    onSuccess: () => {
      setModalInfo(undefined)
      queryCache.invalidateQueries(["rate-card-loadings", id])
    },
    onError: (err: ApiError) => {
      setModalInfo(undefined)
      sendFeedback(t(err?.message), "error")
    },
  })

  const onUpdate = (rowId: string, row: Partial<RateCardLoading>) => {
    patch({
      rateCardId: id,
      id: rowId,
      ...row,
    })
  }

  const onDelete = (rowId: string) => {
    const label = "rate.card.loading"
    setModalInfo({
      title: t(`${label}.list.delete.title`),
      type: "primary_destructive",
      description: t(`${label}.list.delete.description`),
      submitLabel: t(`${label}.list.delete.action`),
      onSubmit: async () => await deleteRow(rowId || ""),
    })
  }

  const onCreate = (newRow: Partial<RateCardLoading>) => {
    return create({ rateCardId: id, ...newRow })
  }

  const getError = (id: string, name: string) => {
    if (
      !!tableErrors?.length &&
      tableErrors.some((e, idx) => e.rowId === id && e.name === name)
    )
      return name
    else return undefined
  }

  return (
    <>
      <div className={styles.container}>
        <div className={styles.row}>
          <div className={cx("BodyBody-1BlackBold", styles.title)}>
            {t("rate.card.loading")}
          </div>
          <Switch
            name="loadind"
            color="primary"
            checked={checked}
            onChange={onSwitchChange}
            disabled={!hasAuthority(["AUTH_EDIT_RATE_CARD"])}
          />
        </div>
        {checked && hasAuthority(["AUTH_EDIT_RATE_CARD"]) ? (
          <Box
            display="inline-flex"
            justifyContent="flex-end"
            className={styles.buttonContainer}
          >
            <Button
              onClick={newRowToggle.onOpen}
              disabled={newRowToggle.open}
              color="primary"
              variant="contained"
            >
              {t("rate.card.loading.create")}
            </Button>
          </Box>
        ) : null}
      </div>
      {checked ? (
        <Rable<RateCardLoading>
          data={loadings || []}
          columns={cols}
          newRowColumns={newRowCols}
          required={requiredCols}
          createRow={onCreate}
          updateRow={onUpdate}
          getError={getError}
          label="rate.card.loading"
          open={newRowToggle.open}
          onClose={newRowToggle.onClose}
          hasActions={actionAllowed}
          actions={(row: RateCardLoading) => [
            {
              label: t("actions.delete"),
              hidden: false,
              onClick: () => onDelete(row?.id || ""),
              labelStyle: styles.deleteLabel,
            },
          ]}
        />
      ) : null}
      {/* <div className={styles.errorMessage}>
        {tableErrors ? tableErrors.map((error: any) => t(error?.key)) : ""}
      </div> */}
      <ConfirmationModal
        open={isModalOpen}
        onCancel={() => setModalInfo(undefined)}
        {...modalInfo}
      />
    </>
  )
}

const styles = {
  container: css`
    display: flex;
    justify-content: space-between;
    padding-bottom: var(--spacing-m);
  `,
  row: css`
    display: inline-flex;
    flex-direction: row;
  `,
  title: css`
    display: flex;
    align-items: center;
  `,
  buttonContainer: css`
    height: 32px;
    align-self: flex-end;
  `,
  errorMessage: css`
    font-size: 13px;
    color: var(--light-red);
    white-space: pre-wrap;
  `,
  deleteLabel: css`
    > span {
      color: var(--red);
    }
  `,
}
